/**
 * Vue Router navigation guard for the /admin/* area.
 *
 * Behaviour:
 *  - Anonymous users: deferred to the global auth guard (which
 *    redirects to /login). This guard runs only for authenticated users.
 *  - Authenticated users with no admin.* groups: AdminApp itself renders
 *    AdminForbiddenScreen — so the guard simply allows the route through
 *    rather than redirecting. We avoid forcing /forbidden, because that
 *    would lose the original URL the user typed.
 *  - When a route requires a specific group via meta.requiredAdminGroup,
 *    the guard redirects to /admin (the dashboard) when the user lacks it.
 *    Showing a 403 inside the panel is more friendly than a hard block.
 */
import type { NavigationGuard } from 'vue-router';
import { useAdminMeStore } from '@/admin/entities/me/store';
import type { AdminGroup } from '@/admin/entities/me/types';

export const requireAdminGroup: NavigationGuard = async (to, from, next) => {
    const me = useAdminMeStore();
    if (!me.me && !me.accessDenied) {
        await me.load();
    }
    const required = to.meta?.requiredAdminGroup as AdminGroup | undefined;
    if (!required) {
        next();
        return;
    }
    if (me.hasGroup(required)) {
        next();
        return;
    }
    next({ path: '/admin', query: { denied: required } });
};
